/* Self-contained poster rotation. No app code, commerce actions or network requests. */
if (!customElements.get('bc-poster-player')) {
  customElements.define('bc-poster-player', class extends HTMLElement {
    connectedCallback() {
      this.radios = [...this.querySelectorAll('.bc-poster-choice')];
      if (this.radios.length < 2) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches || this.dataset.autoplay !== 'true';
      this.hovered = false;
      this.focused = false;
      this.editing = Boolean(window.Shopify?.designMode);
      this.interval = Math.max(3000, Number(this.dataset.interval) || 3000);
      this.toggle = this.querySelector('.bc-poster-toggle');
      if (this.toggle) {
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => {
          this.paused = !this.paused;
          this.updateButton();
          this.schedule();
        }, options);
      }
      this.addEventListener('mouseenter', () => { this.hovered = window.matchMedia('(hover: hover)').matches; this.schedule(); }, options);
      this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, options);
      this.addEventListener('focusout', (event) => {
        this.focused = this.contains(event.relatedTarget);
        this.schedule();
      }, options);
      this.addEventListener('keydown', (event) => {
        const control = event.target.closest('.bc-poster-arrow');
        if (!control || !['Enter', ' '].includes(event.key)) return;
        const radio = this.radios.find((item) => item.id === control.htmlFor);
        if (!radio) return;
        event.preventDefault();
        radio.checked = true;
        // Keep focus on a visible control when the previous slide is hidden.
        this.querySelector(`#${radio.getAttribute('aria-controls')} .bc-poster-arrow`)?.focus();
        this.schedule();
      }, options);
      // Horizontal touch gestures change the existing radios; vertical page scrolling stays native.
      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
        this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
      }, options);
      this.addEventListener('pointercancel', () => { this.gesture = null; }, options);
      this.addEventListener('pointerup', (event) => {
        const start = this.gesture; this.gesture = null;
        if (!start || event.pointerId !== start.id) return;
        const dx = event.clientX - start.x, dy = event.clientY - start.y;
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
        const current = this.radios.findIndex((radio) => radio.checked);
        const rtl = getComputedStyle(this).direction === 'rtl';
        const direction = (dx < 0 ? 1 : -1) * (rtl ? -1 : 1);
        this.radios[(current + direction + this.radios.length) % this.radios.length].checked = true;
        this.schedule();
        // Suppress the click synthesized from a swipe starting on a product link.
        const until = performance.now() + 350;
        this.addEventListener('click', (click) => { if (performance.now() < until) { click.preventDefault(); click.stopPropagation(); } }, { capture: true, once: true, signal: this.abort.signal });
      }, options);
      this.addEventListener('change', () => this.schedule(), options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', () => {
        this.paused = this.motion.matches;
        this.updateButton();
        this.schedule();
      }, options);
      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('.bc-poster-slide');
        const radio = slide && this.radios.find((item) => item.getAttribute('aria-controls') === slide.id);
        if (radio) radio.checked = true;
        this.editing = true;
        this.schedule();
      }, options);
      this.addEventListener('shopify:block:deselect', () => { this.editing = Boolean(window.Shopify?.designMode); this.schedule(); }, options);
      this.updateButton();
      this.schedule();
    }

    updateButton() {
      if (!this.toggle) return;
      this.toggle.setAttribute('aria-label', this.paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      this.toggle.querySelector('span').textContent = this.paused ? '▶' : '⏸';
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.paused || this.hovered || this.focused || this.editing || document.hidden) return;
      this.timer = setTimeout(() => {
        const current = this.radios.findIndex((radio) => radio.checked);
        this.radios[(current + 1) % this.radios.length].checked = true;
        this.schedule();
      }, this.interval);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.abort?.abort();
    }
  });
}
